import React from 'react';
import {
  Row,
  Col,
  Space,
  Typography,
  Select,
  Switch,
  InputNumber,
  Input
} from 'antd';
import { Field, FieldHelperProps, FieldInputProps, FormikProps } from 'formik';
import PropTypes from 'prop-types';
import classes from './Style.module.scss';
import { DataType, useMasterData } from 'app/providers/StaticProvider';
import get from 'lodash/get';
import Modal from 'app/components/Modal';
import StyledAntdTooltip from 'app/components/Tooltip';
import { InfoCircleOutlined } from '@ant-design/icons';
import { TOOLTIP_CONTENT } from './constants';

const FIELD_NAME = {
  CRITICAL_LEVEL: 'criticalLevel',
  MAJOR_LEVEL: 'criticalMajorLevel',
  MINOR_LEVEL: 'criticalMinorLevel'
};

type BaseProps = {
  helperText?: string;
  fieldNames: {
    critical: string;
    major: string;
    minor: string;
  };
  sampleSizeFieldName?: {
    critical: string;
    major: string;
    minor: string;
  };
  defectiveAcceptedFieldName?: {
    critical: string;
    major: string;
    minor: string;
  };
};

type InspectionAQLWithoutCustomSamplingProps = BaseProps & {
  canCustomized?: false;
  customSamplingFieldname?: string;
  onDisableCustom?: undefined;
};

type InspectionAQLWithCustomSamplingProps = BaseProps & {
  canCustomized: true;
  customSamplingFieldname: string;
  onDisableCustom?: (() => void) | undefined;
};

type InspectionAQLProps =
  | InspectionAQLWithoutCustomSamplingProps
  | InspectionAQLWithCustomSamplingProps;

export default function InspectionAQL({
  fieldNames = {
    critical: FIELD_NAME.CRITICAL_LEVEL,
    major: FIELD_NAME.MAJOR_LEVEL,
    minor: FIELD_NAME.MINOR_LEVEL
  },
  helperText,
  customSamplingFieldname,
  sampleSizeFieldName,
  onDisableCustom,
  defectiveAcceptedFieldName,
  canCustomized = false
}: InspectionAQLProps) {
  const { getOptions } = useMasterData();
  const AQLOptions = getOptions(DataType.AQL);
  const { critical, major, minor } = fieldNames;
  // Use the fieldNames variables here if needed
  const isCustomSampling = (values) => {
    return get(values, customSamplingFieldname, false);
  };

  const FieldFactory =
    (values) =>
    // eslint-disable-next-line react/display-name
    (
      field: FieldInputProps<number>,
      options = {
        type: null,
        inputOptions: {},
        selectOptions: {}
      }
    ) => {
      const isCustomSamplingValue = isCustomSampling(values);

      const Element = !isCustomSamplingValue ? (
        <Select
          options={AQLOptions}
          placeholder="Select Critical Level"
          value={field.value}
          onBlur={field.onBlur}
          onChange={(option) => {
            field.onChange({
              target: {
                name: field.name,
                value: option
              }
            });
          }}
          {...options.selectOptions}
        />
      ) : (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Select Critical Level"
          value={field.value}
          onBlur={field.onBlur}
          onChange={(value) => {
            field.onChange({
              target: {
                name: field.name,
                value
              }
            });
          }}
          {...options.inputOptions}
        />
      );

      if (options.type === 'Select') {
        return (
          <Select
            options={AQLOptions}
            placeholder="Select Critical Level"
            value={field.value}
            onBlur={field.onBlur}
            onChange={(option) => {
              field.onChange({
                target: {
                  name: field.name,
                  value: option
                }
              });
            }}
            {...options.selectOptions}
          />
        );
      }

      return Element;
    };

  const renderExtraAQL = (groupFieldName) => {
    return (
      <Row gutter={[16, 16]} wrap>
        <Col sm={24} lg={8}>
          <Space direction="vertical" className={classes.fullWidth} size={4}>
            <label className={classes.fieldLabel}>Critical Level</label>
            <Field name={groupFieldName.critical}>
              {({ field, form }) => {
                const element = FieldFactory(form.values)(field, {
                  type: null,
                  inputOptions: {
                    placeholder: 'Select Critical Level'
                  },
                  selectOptions: {}
                });
                return element;
              }}
            </Field>
          </Space>
        </Col>
        <Col sm={24} lg={8}>
          <Space direction="vertical" className={classes.fullWidth} size={4}>
            <label className={classes.fieldLabel}>Major Level</label>
            <Field name={groupFieldName.major}>
              {({ field, form }) => {
                const element = FieldFactory(form.values)(field, {
                  type: null,
                  inputOptions: {
                    placeholder: 'Select Major Level'
                  },
                  selectOptions: {}
                });
                return element;
              }}
            </Field>
          </Space>
        </Col>
        <Col sm={24} lg={8}>
          <Space direction="vertical" className={classes.fullWidth} size={4}>
            <label className={classes.fieldLabel}>Minor Level</label>
            <Field name={groupFieldName.minor}>
              {({ field, form }) => {
                const element = FieldFactory(form.values)(field, {
                  type: null,
                  inputOptions: {
                    placeholder: 'Select Minor Level'
                  },
                  selectOptions: {}
                });
                return element;
              }}
            </Field>
          </Space>
        </Col>
      </Row>
    );
  };

  return (
    <div className={classes.block}>
      <span>
        <span className={classes.titleAndSwitch}>
          <Typography.Title className={classes.subTitle} level={4}>
            Inspection AQL
          </Typography.Title>
          {canCustomized && (
            <Field name={customSamplingFieldname}>
              {({ field }) => {
                return (
                  <Space>
                    <Switch
                      title="Customize Sampling Plan"
                      checked={field.value}
                      onChange={(checked) => {
                        if (checked) {
                          field.onChange({
                            target: {
                              name: field.name,
                              value: checked
                            }
                          });
                        } else {
                          Modal.danger({
                            icon: null,
                            closable: false,
                            width: 500,
                            title:
                              'Are you sure to turn off Customize Sampling Plan?',
                            content:
                              'It will revert to automatically calculated values.',
                            okText: 'Confirm',
                            wrapClassName: classes.modal,
                            onOk: () => {
                              field.onChange({
                                target: {
                                  name: field.name,
                                  value: checked
                                }
                              });
                              if (onDisableCustom) {
                                onDisableCustom();
                              }
                            }
                          });
                        }
                      }}
                    />
                    Customize Sampling Plan
                    <StyledAntdTooltip
                      title={TOOLTIP_CONTENT[field.value || false]}
                    >
                      <InfoCircleOutlined />
                    </StyledAntdTooltip>
                  </Space>
                );
              }}
            </Field>
          )}
        </span>
        {helperText && <p className={classes.helper}>{helperText}</p>}
      </span>

      <Row gutter={[16, 16]} wrap>
        <Col sm={24} lg={8}>
          <Space direction="vertical" className={classes.fullWidth} size={4}>
            <label className={classes.fieldLabel}>Critical Level</label>
            <Field name={critical}>
              {({ field, form }) => {
                const element = FieldFactory(form.values)(field, {
                  type: 'Select',
                  inputOptions: {},
                  selectOptions: {
                    disabled: isCustomSampling(form.values),
                    placeholder: 'Select Critical Level'
                  }
                });
                return element;
              }}
            </Field>
          </Space>
        </Col>
        <Col sm={24} lg={8}>
          <Space direction="vertical" className={classes.fullWidth} size={4}>
            <label className={classes.fieldLabel}>Major Level</label>
            <Field name={major}>
              {({ field, form }) => {
                const element = FieldFactory(form.values)(field, {
                  type: 'Select',
                  inputOptions: {},
                  selectOptions: {
                    disabled: isCustomSampling(form.values),
                    placeholder: 'Select Major Level'
                  }
                });
                return element;
              }}
            </Field>
          </Space>
        </Col>
        <Col sm={24} lg={8}>
          <Space direction="vertical" className={classes.fullWidth} size={4}>
            <label className={classes.fieldLabel}>Minor Level</label>
            <Field name={minor}>
              {({ field, form }) => {
                const element = FieldFactory(form.values)(field, {
                  type: 'Select',
                  inputOptions: {},
                  selectOptions: {
                    disabled: isCustomSampling(form.values),
                    placeholder: 'Select Minor Level'
                  }
                });
                return element;
              }}
            </Field>
          </Space>
        </Col>
      </Row>
      {sampleSizeFieldName && (
        <div className={classes.extraSection}>
          <span>
            <span className={classes.titleAndSwitch}>
              <Typography.Title className={classes.subTitle} level={4}>
                Sample Size
              </Typography.Title>
            </span>
          </span>
          {renderExtraAQL(sampleSizeFieldName)}
        </div>
      )}
      {defectiveAcceptedFieldName && (
        <div className={classes.extraSection}>
          <span>
            <span className={classes.titleAndSwitch}>
              <Typography.Title className={classes.subTitle} level={4}>
                Defectives Accepted
              </Typography.Title>
            </span>
          </span>
          {renderExtraAQL(defectiveAcceptedFieldName)}
        </div>
      )}
    </div>
  );
}

InspectionAQL.defaultProps = {
  fieldNames: {
    critical: FIELD_NAME.CRITICAL_LEVEL,
    major: FIELD_NAME.MAJOR_LEVEL,
    minor: FIELD_NAME.MINOR_LEVEL
  },
  helperText: ''
};
